import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { TooltipModule } from 'ngx-bootstrap/tooltip';

import type { FormInputSettingsData } from '@sites/entity-core';

import type { ColorPickerEventPayload } from '@components/sp-color-picker/src/types/color-picker.type';
import { CSS_RESTRICTIONS } from '@common/constants';
import { FormStyleEnum, InputBorderEnum } from '@common/enums';
import { ERadioGroupType } from '@ui/sp-radio-group';

import { ThemeStateService } from '@sites/site-theme/src/state';

import { SpColorPickerComponent } from '../sp-color-picker/src/sp-color-picker.component';
import { SpInputNumComponent } from '../sp-form-controls/sp-input-num/sp-input-num.component';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { SpSwitchComponent } from '../../ui/sp-switch/src/sp-switch.component';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { SpRadioGroupComponent } from '../../ui/sp-radio-group/src/sp-radio-group.component';

/**
 * Signal-form editor for the form widget's input/modal styling — the signal
 * twin of the FormGroup-based `sp-form-input-settings`. Mirrors that template
 * (modal block, static-form block with labels/style/appearance/colours/
 * radius/size, modal-form block) with the value-in
 * (`FormInputSettingsData` from `@sites/entity-core`) /
 * `valueChangeEvent`-out contract.
 *
 * The login-edu-specific `widget`-driven branch of the original is replaced
 * by the plain `isEduModalWidget` input so the lib stays app-type-free.
 */
@Component({
    selector: 'sp-form-input-settings-signal',
    templateUrl: './sp-form-input-settings-signal.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        TranslateModule,
        TooltipModule,
        SpColorPickerComponent,
        SpInputNumComponent,
        SpSwitchComponent,
        SpRadioGroupComponent,
    ],
})
export class SpFormInputSettingsSignalComponent {
    private readonly themeState = inject(ThemeStateService);

    public readonly settings = input<FormInputSettingsData | null>(null);
    public readonly isPaymentForm = input<boolean>(false);
    public readonly isModalForm = input<boolean>(false);
    public readonly isTwoStepForm = input<boolean>(false);
    public readonly isLoginForm = input<boolean>(false);
    public readonly isCartForm = input<boolean>(false);
    public readonly isEduModalWidget = input<boolean>(false);

    public readonly valueChangeEvent = output<FormInputSettingsData>();

    public readonly inputBorderEnum = InputBorderEnum;
    public readonly formStyleEnum = FormStyleEnum;
    public readonly radioGroupType = ERadioGroupType;

    public readonly selectedThemeColors = this.themeState.selectedColors;

    public readonly defaultModalBackgroundColor = '#fbfcfc';

    public readonly BORDER_RADIUS_MIN = CSS_RESTRICTIONS.BORDER_RADIUS_MIN;
    public readonly BORDER_RADIUS_MAX = CSS_RESTRICTIONS.BORDER_RADIUS_MAX;
    public readonly PADDING_MIN = CSS_RESTRICTIONS.PADDING_MIN;
    public readonly PADDING_MAX = CSS_RESTRICTIONS.PADDING_MAX;

    private readonly model = signal<FormInputSettingsData | null>(null);

    public readonly backgroundColor = computed(() => this.model()?.backgroundColor);
    public readonly borderColor = computed(() => this.model()?.borderColor);
    public readonly linkColor = computed(() => this.model()?.linkColor);
    public readonly inputSize = computed(() => this.model()?.inputSize);
    public readonly textColor = computed(() => this.model()?.textColor);
    public readonly inputTextColor = computed(() => this.model()?.inputTextColor);
    public readonly borderRadius = computed(() => this.model()?.borderRadius);
    public readonly inputBorderType = computed(() => this.model()?.inputBorderType);
    public readonly formStyleType = computed(() => this.model()?.formStyleType);
    public readonly showLabels = computed(() => Boolean(this.model()?.showLabels));
    public readonly modalBackgroundColor = computed(() => this.model()?.modalBackgroundColor);
    public readonly modalBorderRadius = computed(() => this.model()?.modalBorderRadius);
    public readonly modalPadding = computed(() => this.model()?.modalPadding);
    public readonly modalTitleTextColor = computed(() => this.model()?.modalTitleTextColor);
    public readonly modalShowLabels = computed(() => Boolean(this.model()?.modalShowLabels));
    public readonly modalInputBorderType = computed(() => this.model()?.modalInputBorderType);
    public readonly modalInputBackgroundColor = computed(() => this.model()?.modalInputBackgroundColor);
    public readonly modalInputBorderColor = computed(() => this.model()?.modalInputBorderColor);
    public readonly modalTextColor = computed(() => this.model()?.modalTextColor);
    public readonly modalInputTextColor = computed(() => this.model()?.modalInputTextColor);
    public readonly modalInputBorderRadius = computed(() => this.model()?.modalInputBorderRadius);
    public readonly modalInputSize = computed(() => this.model()?.modalInputSize);

    public readonly isDisabled = computed(() => this.inputBorderType() === InputBorderEnum.BOTTOM_BORDER);

    constructor() {
        effect(() => {
            const incoming = this.settings();
            if (!incoming) return;
            this.model.set(incoming);
        });
    }

    // --- Setters (template event handlers) ---

    public setTextColor({ value, presetName }: ColorPickerEventPayload): void {
        this.patch({ textColor: presetName ?? value });
    }

    public setModalTextColor({ value, presetName }: ColorPickerEventPayload): void {
        this.patch({ modalTextColor: presetName ?? value });
    }

    public setModalTitleTextColor({ value, presetName }: ColorPickerEventPayload): void {
        this.patch({ modalTitleTextColor: presetName ?? value });
    }

    public setInputTextColor({ value, presetName }: ColorPickerEventPayload): void {
        this.patch({ inputTextColor: presetName ?? value });
    }

    public setModalInputTextColor({ value, presetName }: ColorPickerEventPayload): void {
        this.patch({ modalInputTextColor: presetName ?? value });
    }

    public setThemeBackgroundColor({ value, presetName }: ColorPickerEventPayload): void {
        this.patch({ backgroundColor: presetName ?? value });
    }

    public setThemeModalInputBackgroundColor({ value, presetName }: ColorPickerEventPayload): void {
        this.patch({ modalInputBackgroundColor: presetName ?? value });
    }

    public setBorderColor({ value, presetName }: ColorPickerEventPayload): void {
        this.patch({ borderColor: presetName ?? value });
    }

    public setModalInputBorderColor({ value, presetName }: ColorPickerEventPayload): void {
        this.patch({ modalInputBorderColor: presetName ?? value });
    }

    public setLinkColor({ value, presetName }: ColorPickerEventPayload): void {
        this.patch({ linkColor: presetName ?? value });
    }

    public setModalBackgroundColor({ value, presetName }: ColorPickerEventPayload): void {
        this.patch({ modalBackgroundColor: presetName ?? value });
    }

    public setIsShowFieldsName(value: boolean): void {
        this.patch({ showLabels: value });
    }

    public setIsModalShowFieldsName(value: boolean): void {
        this.patch({ modalShowLabels: value });
    }

    public setInputSize(value: string): void {
        this.patch({ inputSize: value as FormInputSettingsData['inputSize'] });
    }

    public setModalInputSize(value: string): void {
        this.patch({ modalInputSize: value as FormInputSettingsData['modalInputSize'] });
    }

    public setInputBorderType(borderType: InputBorderEnum): void {
        // Bottom-border inputs can't have a radius — mirror the FormGroup
        // component's reset.
        const patch: Partial<FormInputSettingsData> = { inputBorderType: borderType };
        if (borderType === InputBorderEnum.BOTTOM_BORDER) {
            patch.borderRadius = 0;
        }
        this.patch(patch);
    }

    public setModalInputBorderType(borderType: InputBorderEnum): void {
        const patch: Partial<FormInputSettingsData> = { modalInputBorderType: borderType };
        if (borderType === InputBorderEnum.BOTTOM_BORDER) {
            patch.modalBorderRadius = 0;
        }
        this.patch(patch);
    }

    public setFormStyleType(formStyleType: FormStyleEnum): void {
        this.patch({ formStyleType });
    }

    public setBorderRadius(value: number): void {
        this.patch({ borderRadius: value });
    }

    public setModalInputBorderRadius(value: number): void {
        this.patch({ modalInputBorderRadius: value });
    }

    public setModalBorderRadius(value: number): void {
        this.patch({ modalBorderRadius: value });
    }

    public setModalPadding(value: number): void {
        this.patch({ modalPadding: value });
    }

    private patch(patch: Partial<FormInputSettingsData>): void {
        const current = this.model();
        if (!current) return;
        const next = { ...current, ...patch };
        this.model.set(next);
        this.valueChangeEvent.emit(next);
    }
}
